// Interactive particle/wireframe rendition of ZEUS (Theseus 26000),
// generated offline from the real STEP assembly.
// Hovering turns the cloud solid and highlights the subsystem under the
// cursor with a callout (data in zeus-info.js, geometry ids baked into
// the points binary).
import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  Points,
  LineSegments,
  Group,
  Color,
  Vector2,
  Vector3,
} from "three";
import { SUBSYSTEMS } from "./zeus-info.js";

const POINTS_URL = new URL("../assets/zeus-points.bin", import.meta.url);
const LINES_URL = new URL("../assets/zeus-lines.bin", import.meta.url);

const INK = new Color("#171611");
const BLUE = new Color("#2336f0");

let rngState = 987654321;
function rng() {
  rngState = (rngState * 1664525 + 1013904223) >>> 0;
  return rngState / 4294967296;
}

async function fetchBuf(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${url} → ${res.status}`);
  return res.arrayBuffer();
}

const vertexCommon = `
  uniform float uProgress;
  uniform float uTime;
  uniform vec2 uMouse;
  uniform float uRepel;
  uniform float uHoverMix;
  attribute vec3 aScatter;
  attribute float aRand;

  vec3 assembled(vec3 base) {
    float t = clamp((uProgress - aRand * 0.38) / 0.62, 0.0, 1.0);
    float e = 1.0 - pow(1.0 - t, 3.0);
    vec3 p = mix(aScatter, base, e);
    p += 0.0035 * (1.0 - uHoverMix * 0.8) * vec3(
      sin(uTime * 0.9 + aRand * 31.4),
      cos(uTime * 1.1 + aRand * 17.2),
      sin(uTime * 0.7 + aRand * 23.9)
    ) * (0.3 + 0.7 * aRand);
    return p;
  }

  vec4 repelled(vec4 clipPos) {
    vec2 ndc = clipPos.xy / clipPos.w;
    vec2 d = ndc - uMouse;
    float dist = length(d);
    float push = smoothstep(0.22, 0.0, dist) * uRepel * (1.0 - uHoverMix * 0.7);
    clipPos.xy += normalize(d + 1e-5) * push * clipPos.w * 0.016;
    return clipPos;
  }
`;

const pointsVert = `
  __COMMON__
  uniform float uSize;
  uniform float uPxRatio;
  uniform float uHover;
  attribute float aSys;
  varying float vRand;
  varying float vDepth;
  varying float vHi;

  void main() {
    vRand = aRand;
    vHi = (uHover > -0.5 && abs(aSys - uHover) < 0.5) ? 1.0 : 0.0;
    vec4 mv = modelViewMatrix * vec4(assembled(position), 1.0);
    vDepth = clamp((-mv.z - 2.2) / 3.2, 0.0, 1.0);
    gl_Position = repelled(projectionMatrix * mv);
    float size = uSize * (1.0 + vHi * uHoverMix * 0.55);
    gl_PointSize = size * uPxRatio * (2.6 / -mv.z) * (0.65 + 0.7 * aRand);
  }
`.replace("__COMMON__", vertexCommon);

const pointsFrag = `
  precision highp float;
  uniform vec3 uInk;
  uniform vec3 uBlue;
  uniform float uOpacity;
  uniform float uHoverMix;
  varying float vRand;
  varying float vDepth;
  varying float vHi;

  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float edge = smoothstep(0.5, 0.32, d);
    vec3 col = mix(uInk, uBlue, step(0.82, vRand));
    // hovered subsystem turns blue and fully opaque; the rest recedes
    col = mix(col, uBlue, vHi * uHoverMix);
    float alpha = uOpacity * mix(1.0, 0.35, vDepth);
    alpha *= mix(1.0, 0.3, uHoverMix * (1.0 - vHi));
    alpha = mix(alpha, 0.95, vHi * uHoverMix * (1.0 - vDepth * 0.5));
    gl_FragColor = vec4(col, alpha * edge);
  }
`;

const linesVert = `
  __COMMON__
  varying float vDepth;

  void main() {
    vec4 mv = modelViewMatrix * vec4(assembled(position), 1.0);
    vDepth = clamp((-mv.z - 2.2) / 3.2, 0.0, 1.0);
    gl_Position = repelled(projectionMatrix * mv);
  }
`.replace("__COMMON__", vertexCommon);

const linesFrag = `
  precision mediump float;
  uniform vec3 uInk;
  uniform float uOpacity;
  varying float vDepth;

  void main() {
    float alpha = uOpacity * mix(1.0, 0.25, vDepth);
    gl_FragColor = vec4(uInk, alpha);
  }
`;

export async function createZeus(canvas, { reduced = false } = {}) {
  const isMobile = window.innerWidth < 760;
  const finePointer = window.matchMedia(
    "(hover: hover) and (pointer: fine)"
  ).matches;

  let pointsBuf, linesBuf;
  try {
    [pointsBuf, linesBuf] = await Promise.all([
      fetchBuf(POINTS_URL),
      fetchBuf(LINES_URL).catch(() => null),
    ]);
  } catch (e) {
    console.warn("zeus asset unavailable:", e);
    return null;
  }

  const renderer = new WebGLRenderer({
    canvas,
    alpha: true,
    antialias: !isMobile,
    powerPreference: "high-performance",
  });
  renderer.setClearColor(0x000000, 0);

  const scene = new Scene();
  const camera = new PerspectiveCamera(32, 1, 0.1, 50);
  camera.position.set(0, 0.12, 4.4);

  const group = new Group();
  const inner = new Group();
  inner.rotation.x = -Math.PI / 2; // assembly is Z-up (verified visually)
  group.add(inner);
  scene.add(group);

  const uniforms = {
    uProgress: { value: 0 },
    uTime: { value: 0 },
    uMouse: { value: new Vector2(10, 10) },
    uRepel: { value: finePointer ? 1 : 0 },
    uSize: { value: isMobile ? 1.05 : 1.3 },
    uPxRatio: { value: 1 },
    uInk: { value: INK },
    uBlue: { value: BLUE },
    uOpacity: { value: 0.72 },
    uHoverMix: { value: 0 },
    uHover: { value: -1 },
  };
  const lineUniforms = {
    ...uniforms,
    uOpacity: { value: 0.1 },
  };

  // ---- points: 10 bytes each (int16 xyz · int8 normal · uint8 sys) ----
  const pHead = new Uint32Array(pointsBuf, 0, 1)[0];
  const pView = new DataView(pointsBuf, 4);
  const stride = isMobile ? 2 : 1;
  const pCount = Math.floor(pHead / stride);
  const pos = new Float32Array(pCount * 3);
  const nrm = new Float32Array(pCount * 3);
  const sys = new Float32Array(pCount);
  const scatter = new Float32Array(pCount * 3);
  const rand = new Float32Array(pCount);
  for (let i = 0; i < pCount; i++) {
    const o = i * stride * 10;
    pos[i * 3] = pView.getInt16(o, true) / 32760;
    pos[i * 3 + 1] = pView.getInt16(o + 2, true) / 32760;
    pos[i * 3 + 2] = pView.getInt16(o + 4, true) / 32760;
    nrm[i * 3] = pView.getInt8(o + 6) / 127;
    nrm[i * 3 + 1] = pView.getInt8(o + 7) / 127;
    nrm[i * 3 + 2] = pView.getInt8(o + 8) / 127;
    sys[i] = pView.getUint8(o + 9);
    const theta = rng() * Math.PI * 2;
    const phi = Math.acos(2 * rng() - 1);
    const r = 2.2 + rng() * 2.2;
    scatter[i * 3] = Math.sin(phi) * Math.cos(theta) * r;
    scatter[i * 3 + 1] = Math.sin(phi) * Math.sin(theta) * r;
    scatter[i * 3 + 2] = Math.cos(phi) * r;
    rand[i] = rng();
  }

  const pGeo = new BufferGeometry();
  pGeo.setAttribute("position", new BufferAttribute(pos, 3));
  pGeo.setAttribute("aNormal", new BufferAttribute(nrm, 3));
  pGeo.setAttribute("aSys", new BufferAttribute(sys, 1));
  pGeo.setAttribute("aScatter", new BufferAttribute(scatter, 3));
  pGeo.setAttribute("aRand", new BufferAttribute(rand, 1));

  const pMat = new ShaderMaterial({
    uniforms,
    vertexShader: pointsVert,
    fragmentShader: pointsFrag,
    transparent: true,
    depthWrite: false,
  });
  inner.add(new Points(pGeo, pMat));

  // ---- lines (desktop only) ----
  if (linesBuf && !isMobile) {
    const lCount = new Uint32Array(linesBuf, 0, 1)[0];
    const lData = new Int16Array(linesBuf, 4);
    const lPos = new Float32Array(lCount * 6);
    const lScatter = new Float32Array(lCount * 6);
    const lRand = new Float32Array(lCount * 2);
    for (let s = 0; s < lCount; s++) {
      const theta = rng() * Math.PI * 2;
      const phi = Math.acos(2 * rng() - 1);
      const r = 2.4 + rng() * 2.4;
      const ox = Math.sin(phi) * Math.cos(theta) * r;
      const oy = Math.sin(phi) * Math.sin(theta) * r;
      const oz = Math.cos(phi) * r;
      const rr = rng();
      for (let e = 0; e < 2; e++) {
        const o = s * 6 + e * 3;
        lPos[o] = lData[o] / 32760;
        lPos[o + 1] = lData[o + 1] / 32760;
        lPos[o + 2] = lData[o + 2] / 32760;
        lScatter[o] = lPos[o] * 0.25 + ox;
        lScatter[o + 1] = lPos[o + 1] * 0.25 + oy;
        lScatter[o + 2] = lPos[o + 2] * 0.25 + oz;
      }
      lRand[s * 2] = rr;
      lRand[s * 2 + 1] = rr;
    }
    const lGeo = new BufferGeometry();
    lGeo.setAttribute("position", new BufferAttribute(lPos, 3));
    lGeo.setAttribute("aScatter", new BufferAttribute(lScatter, 3));
    lGeo.setAttribute("aRand", new BufferAttribute(lRand, 1));
    const lMat = new ShaderMaterial({
      uniforms: lineUniforms,
      vertexShader: linesVert,
      fragmentShader: linesFrag,
      transparent: true,
      depthWrite: false,
    });
    inner.add(new LineSegments(lGeo, lMat));
  }

  // ---- layout / resize ----
  function layout() {
    const w = canvas.clientWidth || 1;
    const h = canvas.clientHeight || 1;
    const mobile = w < 760;
    const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2);
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    uniforms.uPxRatio.value = dpr;
    if (mobile) {
      const span =
        2 * camera.position.z * Math.tan(((camera.fov / 2) * Math.PI) / 180) *
        camera.aspect;
      const s = Math.min(0.6, (span * 0.82) / 2.2);
      group.position.set(0, 0.55, 0);
      group.scale.setScalar(s);
    } else {
      group.position.set(0.62, 0.16, 0);
      group.scale.setScalar(1.0);
    }
  }
  layout();
  window.addEventListener("resize", layout);

  // ---- interaction: parallax + drag with inertia ----
  const rot = { x: -0.15, y: 5.6 }; // resting 3/4 view (verified visually)
  const target = { x: -0.15, y: 5.6 };
  let autoSpin = 0.06;
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let velY = 0;
  const pointerNdc = new Vector2(10, 10);

  canvas.addEventListener("pointerdown", (e) => {
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    velY = 0;
    canvas.style.cursor = "grabbing";
  });
  window.addEventListener("pointermove", (e) => {
    const rect = canvas.getBoundingClientRect();
    pointerNdc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -(((e.clientY - rect.top) / rect.height) * 2 - 1)
    );
    uniforms.uMouse.value.copy(pointerNdc);

    if (dragging) {
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      target.y += dx * 0.006;
      target.x += dy * 0.004;
      target.x = Math.max(-0.9, Math.min(0.9, target.x));
      velY = dx * 0.006;
    }
  });
  window.addEventListener("pointerup", () => {
    dragging = false;
    canvas.style.cursor = "grab";
  });
  canvas.style.cursor = "grab";

  // ---- subsystem hover (fine pointers only) ----
  // picking works on a subsample of the real points: project them and
  // find the closest one to the cursor. accurate and overlap-proof.
  const callout = document.querySelector("[data-zeus-callout]");
  const calloutNo = callout?.querySelector("[data-callout-no]");
  const calloutName = callout?.querySelector("[data-callout-name]");
  const calloutDesc = callout?.querySelector("[data-callout-desc]");
  const tmpV = new Vector3();
  const anchor = new Vector3(); // raw model space of hovered geometry
  let hoverSys = -1;
  let hoverTarget = 0;

  const PICK_PER_SYS = 320;
  const pickPts = []; // {sys, v: Vector3}
  {
    const perSysCount = {};
    for (let i = 0; i < pCount; i++) {
      const s = sys[i];
      if (!SUBSYSTEMS[s]) continue;
      perSysCount[s] = (perSysCount[s] || 0) + 1;
    }
    const perSysStep = {};
    for (const [s, n] of Object.entries(perSysCount))
      perSysStep[s] = Math.max(1, Math.floor(n / PICK_PER_SYS));
    const seen = {};
    for (let i = 0; i < pCount; i++) {
      const s = sys[i];
      if (!SUBSYSTEMS[s]) continue;
      seen[s] = (seen[s] || 0) + 1;
      if (seen[s] % perSysStep[s] !== 0) continue;
      pickPts.push({
        sys: s,
        v: new Vector3(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]),
      });
    }
  }

  function updateHover() {
    if (api.forceHover != null) {
      setHover(api.forceHover, true);
      return;
    }
    if (!finePointer || dragging || uniforms.uProgress.value < 0.95) {
      setHover(-1);
      return;
    }
    if (Math.abs(pointerNdc.x) > 1 || Math.abs(pointerNdc.y) > 1) {
      setHover(-1);
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const px = ((pointerNdc.x + 1) / 2) * rect.width;
    const py = ((1 - pointerNdc.y) / 2) * rect.height;
    const PICK = 26 * 26; // px² — subsystem selection
    const NEAR = 85 * 85; // px² — "over the robot" → solid
    let best = -1;
    let bestD = PICK;
    let nearest = Infinity;
    let bx = 0, by = 0, bz = 0;
    for (const p of pickPts) {
      tmpV.copy(p.v).applyMatrix4(inner.matrixWorld).project(camera);
      const sx = ((tmpV.x + 1) / 2) * rect.width;
      const sy = ((1 - tmpV.y) / 2) * rect.height;
      const d = (sx - px) * (sx - px) + (sy - py) * (sy - py);
      if (d < nearest) nearest = d;
      if (d < bestD) {
        bestD = d;
        best = p.sys;
        bx = p.v.x; by = p.v.y; bz = p.v.z;
      }
    }
    if (best >= 0) anchor.set(bx, by, bz);
    setHover(best, nearest < NEAR);
  }

  function setHover(id) {
    hoverTarget = id >= 0 ? 1 : 0;
    if (id === hoverSys) return;
    hoverSys = id;
    uniforms.uHover.value = id;
    if (!callout) return;
    if (id >= 0) {
      const info = SUBSYSTEMS[id];
      calloutNo.textContent = `sys. ${info.no}`;
      calloutName.textContent = info.name;
      calloutDesc.textContent = info.desc;
      callout.classList.add("is-on");
      canvas.style.cursor = "crosshair";
    } else {
      callout.classList.remove("is-on");
      canvas.style.cursor = dragging ? "grabbing" : "grab";
    }
  }

  function updateCallout() {
    if (!callout || hoverSys < 0) return;
    tmpV.copy(anchor).applyMatrix4(inner.matrixWorld).project(camera);
    const rect = canvas.getBoundingClientRect();
    const x = ((tmpV.x + 1) / 2) * rect.width + rect.left;
    const y = ((1 - tmpV.y) / 2) * rect.height + rect.top;
    const flip = x > window.innerWidth - 360;
    callout.classList.toggle("is-flipped", flip);
    callout.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  // ---- render loop ----
  let visible = true;
  new IntersectionObserver(([entry]) => (visible = entry.isIntersecting)).observe(
    canvas
  );

  let scrollSpin = 0;
  const clockStart = performance.now();
  let prev = clockStart;

  function frame(now) {
    if (visible && !document.hidden) {
      const dt = Math.min((now - prev) / 1000, 0.05);
      uniforms.uTime.value = (now - clockStart) / 1000;

      if (!dragging) {
        velY *= 0.95;
        // pause the idle spin while inspecting a subsystem
        const spin = hoverSys >= 0 ? 0 : autoSpin;
        target.y += velY + spin * dt;
        target.x += (-0.15 - target.x) * 0.01;
      }
      rot.x += (target.x - rot.x) * 0.08;
      rot.y += (target.y - rot.y) * 0.08;
      group.rotation.x = rot.x;
      group.rotation.y = rot.y + scrollSpin;

      // ease the hover highlight in/out
      uniforms.uHoverMix.value +=
        (hoverTarget - uniforms.uHoverMix.value) * (hoverTarget ? 0.12 : 0.08);
      lineUniforms.uOpacity.value = 0.1 * (1 - uniforms.uHoverMix.value * 0.6);

      group.updateMatrixWorld();
      updateHover();
      updateCallout();

      renderer.render(scene, camera);
    }
    prev = now;
    if (!reduced) requestAnimationFrame(frame);
  }

  if (reduced) {
    uniforms.uProgress.value = 1;
    requestAnimationFrame((n) => frame(n));
  } else {
    requestAnimationFrame(frame);
  }

  const api = {
    uniforms,
    forceHover: null,
    setScrollSpin(v) {
      scrollSpin = v;
    },
    setRotation(x, y) {
      target.x = rot.x = x;
      target.y = rot.y = y;
    },
    stopSpin() {
      autoSpin = 0;
    },
  };
  window.__zeus = api;
  return api;
}
